// Admin console frame — persistent sidebar on >= md, off-canvas drawer
// on small screens. The layout is intentionally lean: brand, nav list,
// current-user badge, logout. Page content is rendered by react-router
// into <Outlet />.
//
// Desktop sidebar behaviour:
//   - Sticky to the viewport (`md:sticky top-0 h-screen`) so the
//     sign-out and user badge stay reachable on tall admin pages
//     without scrolling.
//   - Collapsible via a chevron toggle that always lives in the
//     sidebar header — the same click target works in both states
//     so users do not have to hunt for it after toggling. When
//     collapsed the sidebar shrinks to ~56px, the header switches
//     to a vertical stack (logo above toggle, both centred), and
//     every nav row renders icon-only with the label exposed as
//     `aria-label` (and a `title` tooltip) so links remain clickable
//     and screen-reader-friendly without expanding the rail.
//     Preference is persisted in `localStorage` so the choice
//     survives reloads.
//
// Touch-target note: every interactive element (nav links, menu
// toggle, logout button) is sized to at least 44×44 so the layout is
// usable on a tablet even though the admin audience is primarily
// desktop. See AGENTS.md invariant #10.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react'
import {
  Boxes,
  ClipboardList,
  FileDown,
  LayoutDashboard,
  ListChecks,
  LogOut,
  MapPin,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  X,
} from 'lucide-react'
import { Link, NavLink, Outlet } from 'react-router'
import { BrandHeader } from '../ui/brand-header'

import { useAuth } from './auth-provider'
import {
  isAdminRouteDisabled,
  readDisabledAdminRoutes,
} from '../lib/admin-routes-disabled'
import { ForbiddenBanner } from '../ui/forbidden-banner'
import { ToastProvider } from '../ui/toast'

interface NavItem {
  to: string
  // Route name used as the key for VITE_ADMIN_ROUTES_DISABLED
  // matching. This is the leaf path segment under /admin/ so the
  // env var format ("classification,rooms,reviews") stays obvious
  // to the operator setting it at build time.
  name: string
  label: string
  Icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false'; strokeWidth?: number }>
}

// Order matches PRD §6.1 admin sidebar. Data Import and Users were
// removed from the admin console scope; Imports lives in the backend
// for programmatic use, and user/role management is handled via
// Entra ID groups outside this app.
//
// Templates and Reviews are parent sections with tabbed sub-pages
// (Templates: Room Types / Building Types; Reviews: Review Queue /
// Exclusions). The sidebar entry deep-links into the section's index
// URL; the section layout redirects to its default sub-tab.
export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/admin/dashboard', name: 'dashboard', label: 'Progress Dashboard', Icon: LayoutDashboard },
  { to: '/admin/locations', name: 'locations', label: 'Locations', Icon: MapPin },
  { to: '/admin/assets', name: 'assets', label: 'Assets', Icon: Boxes },
  { to: '/admin/templates', name: 'templates', label: 'Templates', Icon: ClipboardList },
  { to: '/admin/reviews', name: 'reviews', label: 'Reviews', Icon: ListChecks },
  { to: '/admin/exports', name: 'exports', label: 'CSV Export', Icon: FileDown },
]

// ----- Forbidden context (error-boundary-style listener) ----------

export interface ForbiddenContextValue {
  isForbidden: boolean
  reportForbidden: () => void
  dismissForbidden: () => void
}

const ForbiddenContext = createContext<ForbiddenContextValue | undefined>(
  undefined,
)

export function useForbidden(): ForbiddenContextValue {
  const ctx = useContext(ForbiddenContext)
  if (ctx === undefined) {
    // Fallback so isolated unit tests (and any component rendered
    // outside AdminLayout) do not crash. Production admin routes
    // always render inside the layout, so the real provider is
    // always present where it matters.
    return {
      isForbidden: false,
      reportForbidden: () => {},
      dismissForbidden: () => {},
    }
  }
  return ctx
}

function ForbiddenProvider({ children }: { children: ReactNode }) {
  const [isForbidden, setIsForbidden] = useState(false)
  const reportForbidden = useCallback(() => setIsForbidden(true), [])
  const dismissForbidden = useCallback(() => setIsForbidden(false), [])

  return (
    <ForbiddenContext.Provider
      value={{ isForbidden, reportForbidden, dismissForbidden }}
    >
      {children}
    </ForbiddenContext.Provider>
  )
}

function ForbiddenBannerOutlet() {
  const { isForbidden, dismissForbidden } = useForbidden()
  if (!isForbidden) return null
  return (
    <div className="mb-4">
      <ForbiddenBanner onDismiss={dismissForbidden} />
    </div>
  )
}

// ------------------------------------------------------------------

export interface AdminLayoutProps {
  // Injectable for tests. In the app the default (undefined) reads
  // VITE_ADMIN_ROUTES_DISABLED from import.meta.env at render time.
  disabledRoutes?: ReadonlySet<string>
}

const COLLAPSED_STORAGE_KEY = 'cc-admin-sidebar-collapsed'

export function AdminLayout({ disabledRoutes }: AdminLayoutProps = {}) {
  const { user, logout } = useAuth()
  const disabled = disabledRoutes ?? readDisabledAdminRoutes()
  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !isAdminRouteDisabled(item.name, disabled),
  )
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try {
      return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })

  // Body scroll lock — prevent background scrolling while drawer is open
  // (issue 257). Cleaned up on unmount and whenever drawer closes.
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [drawerOpen])

  // Persist the collapsed-rail preference so the choice survives
  // reloads. localStorage may be unavailable in some sandboxed
  // contexts (private mode, embedded webviews) so the write is
  // wrapped in try/catch — UI state still works without persistence.
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(
        COLLAPSED_STORAGE_KEY,
        collapsed ? '1' : '0',
      )
    } catch {
      // Persistence failure is non-fatal — the in-memory state is
      // still authoritative for the current session.
    }
  }, [collapsed])

  const closeDrawer = useCallback(() => setDrawerOpen(false), [])
  const toggleCollapsed = useCallback(
    () => setCollapsed((prev) => !prev),
    [],
  )
  const handleLogout = useCallback(() => {
    logout()
  }, [logout])

  return (
    <div className="flex min-h-screen flex-col bg-surface text-ink md:flex-row">
      {/* Mobile top bar */}
      <header className="flex items-center justify-between border-b border-border bg-white px-4 py-3 pt-safe md:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
          className="inline-flex h-11 w-11 items-center justify-center rounded-pill text-ink hover:bg-surface"
        >
          <Menu aria-hidden="true" strokeWidth={1.5} className="h-5 w-5" />
        </button>
        <Link
          to="/"
          className="inline-flex min-h-11 min-w-11 items-center rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700"
        >
          <BrandHeader variant="topbar" subtitle="Admin Console" />
        </Link>
        <span className="w-11" aria-hidden="true" />
      </header>

      {/* Drawer overlay (mobile) — always rendered; opacity transition
           provides a smooth fade-in/out. aria-hidden + pointer-events-none
           prevents interaction when drawer is closed so the exit animation
           can complete before the element is visually gone (issue 257). */}
      <div
        data-testid="admin-drawer-backdrop"
        className={[
          // Citycare brand v1.0: drawer scrim uses Forest at 40% so it
          // matches the dark-surface signature rather than Tailwind's
          // generic gray-900. The data-testid keeps the presence test
          // class-agnostic (admin-layout.test.tsx, issue 257).
          'fixed inset-0 z-40 bg-brand-deep/40 transition-opacity duration-200 ease-out md:hidden',
          drawerOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* Sidebar — sticky to the viewport on md+ (so sign-out and
           user badge stay reachable on tall pages without scrolling),
           drawer on mobile. Always in the DOM so exit transition
           completes before element is visually gone. aria-hidden +
           pointer-events-none applied when closed to hide from
           assistive technology and block accidental interaction
           (issue 257). Width is collapsible on desktop only — the
           mobile drawer always opens at full nav width. */}
      <aside
        data-testid="admin-sidebar"
        data-collapsed={collapsed ? 'true' : 'false'}
        className={[
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border bg-white transition-[width] duration-150 ease-out md:sticky md:top-0 md:h-screen md:translate-x-0',
          collapsed ? 'md:w-16' : 'md:w-64',
          drawerOpen
            ? 'translate-x-0 transition-transform duration-200 ease-out'
            : '-translate-x-full transition-transform duration-150 ease-out md:translate-x-0',
          !drawerOpen ? 'pointer-events-none md:pointer-events-auto' : '',
        ].join(' ')}
        aria-label="Admin navigation"
        role={drawerOpen ? 'dialog' : undefined}
        aria-modal={drawerOpen ? 'true' : undefined}
        aria-hidden={drawerOpen ? undefined : 'true'}
      >
        <div
          className={[
            // Mobile drawer is always full-width regardless of the
            // persisted desktop preference, so keep the default
            // padding/justify here and only override on md+ when the
            // rail is collapsed. When collapsed at md+, the header
            // switches to a vertical stack so the logo and the
            // collapse toggle both sit at the top of the rail,
            // horizontally centred in the column.
            'flex items-center justify-between border-b border-border px-4 py-4',
            collapsed
              ? 'md:flex-col md:items-center md:justify-start md:gap-2 md:px-2 md:py-3'
              : '',
          ].join(' ')}
        >
          <Link
            to="/"
            onClick={closeDrawer}
            aria-label="Citycare Property"
            className={[
              'inline-flex min-h-11 min-w-11 items-center rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700',
              // Centre the logo mark inside its 44×44 hit-area when
              // the rail is collapsed at md+, so the icon aligns
              // with the centred nav icons below.
              collapsed ? 'md:justify-center' : '',
            ].join(' ')}
          >
            {/* Two brand renderings keep mobile and desktop
                 independent: at < md the wordmark always shows (the
                 drawer is always full width), while at md+ the
                 collapsed rail switches to a logo-only mark. The
                 surrounding Link carries `aria-label` so the
                 accessible name stays stable across both. */}
            <span className={collapsed ? 'md:hidden' : 'inline-flex'}>
              <BrandHeader variant="sidebar" subtitle="Admin Console" />
            </span>
            {collapsed ? (
              <span className="hidden md:inline-flex">
                <BrandHeader variant="sidebar" showWordmark={false} />
              </span>
            ) : null}
          </Link>
          {/* Mobile drawer close button — hidden on md+. */}
          <button
            type="button"
            onClick={closeDrawer}
            aria-label="Close navigation"
            className="inline-flex h-11 w-11 items-center justify-center rounded-pill text-ink hover:bg-surface md:hidden"
          >
            <X aria-hidden="true" strokeWidth={1.5} className="h-5 w-5" />
          </button>
          {/* Desktop collapse / expand toggle — hidden on mobile,
               where the rail always opens at full width via the
               drawer. Always lives in the header so the click target
               does not jump position when the user toggles the rail
               (the previous design moved the toggle to the footer
               when collapsed, which was disorienting). */}
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={collapsed ? 'true' : 'false'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden h-11 w-11 items-center justify-center rounded-pill text-ink hover:bg-surface md:inline-flex"
          >
            {collapsed ? (
              <PanelLeftOpen
                aria-hidden="true"
                strokeWidth={1.5}
                className="h-5 w-5"
              />
            ) : (
              <PanelLeftClose
                aria-hidden="true"
                strokeWidth={1.5}
                className="h-5 w-5"
              />
            )}
          </button>
        </div>

        <nav
          className={[
            // Default padding stays on mobile (drawer is always full
            // width); the tighter md: overrides only kick in when the
            // rail is collapsed on desktop.
            'flex-1 overflow-y-auto p-2',
            collapsed ? 'md:px-1 md:py-2' : '',
          ].join(' ')}
        >
          <ul className="space-y-1">
            {visibleNavItems.map((item) => {
              const Icon = item.Icon
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={closeDrawer}
                    aria-label={item.label}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      [
                        // Mobile drawer always renders the row at full
                        // width with the label; the icon-only md:
                        // overrides only kick in when the rail is
                        // collapsed on desktop.
                        'flex min-h-11 min-w-11 items-center gap-3 rounded-pill px-4 py-2 text-sm font-medium tracking-wide',
                        collapsed
                          ? 'md:min-w-0 md:justify-center md:px-0 md:py-2'
                          : '',
                        isActive
                          ? 'bg-[color:var(--color-brand)] text-[color:var(--color-brand-foreground)]'
                          : 'text-ink-soft hover:bg-surface hover:text-ink',
                      ].join(' ')
                    }
                  >
                    <Icon
                      aria-hidden="true"
                      strokeWidth={1.5}
                      className="h-5 w-5 shrink-0"
                    />
                    <span
                      className={
                        collapsed ? 'md:hidden' : ''
                      }
                    >
                      {item.label}
                    </span>
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </nav>

        <div
          className={[
            // Footer always uses the mobile-friendly padding; the
            // tighter md: overrides only apply when the rail is
            // collapsed on desktop. `pb-safe` is preserved for
            // notched-device support (issue 255), and the arbitrary
            // `pb-[max(...)]` ensures a comfortable bottom gap on
            // non-notched devices where `env(safe-area-inset-bottom)`
            // resolves to `0` (issue: sign-out button visually
            // truncated when the footer hugged the aside edge).
            'border-t border-border px-3 pt-3 pb-safe pb-[max(1rem,env(safe-area-inset-bottom))]!',
            collapsed ? 'md:px-1 md:pt-2' : '',
          ].join(' ')}
        >
          {user ? (
            <div
              className={[
                'mb-1 text-xs',
                collapsed ? 'md:hidden' : '',
              ].join(' ')}
            >
              <div className="truncate font-medium text-ink">
                {user.displayName}
              </div>
            </div>
          ) : null}
          <button
            type="button"
            onClick={handleLogout}
            aria-label="Sign out"
            title={collapsed ? 'Sign out' : undefined}
            className={[
              'inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-pill border border-border bg-white px-3 py-2 text-sm font-medium tracking-wide text-ink hover:bg-surface',
              collapsed ? 'md:px-0 md:py-2' : '',
            ].join(' ')}
          >
            <LogOut aria-hidden="true" strokeWidth={1.5} className="h-4 w-4" />
            <span className={collapsed ? 'md:hidden' : ''}>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content. Surface (`<main>`) keeps `flex-1` so the
           background extends edge-to-edge; the inner wrapper caps
           content at `max-w-6xl` and centres it via `mx-auto` so
           pages do not sprawl on wide screens (page headings used
           to hug the sidebar while justify-between action rows sat
           flush against the viewport's right edge on a 14" Mac). */}
      <main className="flex-1 p-4 md:p-8">
        <div
          data-testid="admin-content-shell"
          className="mx-auto w-full max-w-6xl"
        >
          <ToastProvider>
            <ForbiddenProvider>
              <ForbiddenBannerOutlet />
              <Outlet />
            </ForbiddenProvider>
          </ToastProvider>
        </div>
      </main>
    </div>
  )
}
